import type { FC, ReactNode } from 'react'
import type { ExperimentSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatLogDate } from '@/lib/format'
import ExperimentStatus from '@/components/lab/experiment-status'
import TechnologyList from '@/components/projects/technology-list'
import { SampleBadge } from '@/components/elements/sample-notice'

interface ExperimentItemProps {
    experiment: ExperimentSummary
    compact?: boolean
}

/**
 * One line of the lab log. Reads like a notebook index: catalogue number,
 * title, category, status and the last date it changed.
 */
const ExperimentItem: FC<ExperimentItemProps> = ({ experiment, compact = false }): ReactNode => {
    const lastChange = experiment.updatedAt ?? experiment.date

    return (
        <li className='group relative border-b border-border'>
            <div className={cn('grid grid-cols-4 gap-x-4 gap-y-2.5 sm:gap-x-6 md:grid-cols-12 lg:gap-x-8', compact ? 'py-6' : 'py-7 md:py-8')}>
                <span className='col-span-1 font-mono text-caption text-muted-foreground md:col-span-2 md:pt-1'>
                    {experiment.code}
                </span>

                <div className={cn('col-span-3 flex flex-col gap-2', compact ? 'md:col-span-6' : 'md:col-span-5')}>
                    <h3 className='font-heading text-subheading [font-weight:500] md:text-heading md:[font-weight:450]'>
                        <Link href={`/lab/${experiment.slug}`} className='transition-colors duration-200 after:absolute after:inset-0 group-hover:text-brand'>
                            {experiment.title}
                        </Link>
                    </h3>

                    {!compact && (
                        <p className='max-w-[52ch] text-body text-muted-foreground'>
                            {experiment.description}
                        </p>
                    )}
                </div>

                <div className={cn('col-span-4 flex flex-wrap items-center gap-x-3 gap-y-2 md:flex-col md:items-start md:pt-1.5', compact ? 'md:col-span-2' : 'md:col-span-2')}>
                    <ExperimentStatus status={experiment.status} />
                    {experiment.sample && !compact && <SampleBadge />}
                </div>

                <div className={cn('col-span-4 flex items-start justify-between gap-4 md:pt-1.5', compact ? 'md:col-span-2' : 'md:col-span-3')}>
                    <div className='flex flex-col gap-1.5'>
                        <span className='font-mono text-label uppercase text-muted-foreground'>{experiment.categoryLabel}</span>
                        {!compact && <TechnologyList items={experiment.technologies.slice(0, 3)} />}
                        <time dateTime={lastChange} className='font-mono text-caption text-muted-foreground'>
                            {experiment.updatedAt ? 'Updated ' : 'Started '}{formatLogDate(lastChange)}
                        </time>
                    </div>

                    <ArrowRight
                        aria-hidden='true'
                        className='mt-0.5 size-4 shrink-0 text-muted-foreground transition-[transform,color] duration-300 ease-editorial group-hover:translate-x-1 group-hover:text-brand'
                    />
                </div>
            </div>
        </li>
    )
}

export default ExperimentItem
